import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import '../styles/CardinalsList.css';

// Composants SVG inline
const IconFilter = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
  </svg>
);

const IconSearch = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

const IconThGrid = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="7" height="7" rx="1" />
    <rect x="14" y="3" width="7" height="7" rx="1" />
    <rect x="3" y="14" width="7" height="7" rx="1" />
    <rect x="14" y="14" width="7" height="7" rx="1" />
  </svg>
);

const IconThList = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="5" width="18" height="4" rx="1" />
    <rect x="3" y="13" width="18" height="4" rx="1" />
    <line x1="4" y1="11" x2="8" y2="11" />
    <line x1="4" y1="19" x2="8" y2="19" />
  </svg>
);

const CardinalsList = () => {
  const [cardinaux, setCardinaux] = useState([]);
  const [filteredCardinaux, setFilteredCardinaux] = useState([]);
  const [allCardinaux, setAllCardinaux] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [paysFilter, setPaysFilter] = useState('');
  const [continentFilter, setContinentFilter] = useState('');
  const [ordreFilter, setOrdreFilter] = useState('');
  const [papabileFilter, setPapabileFilter] = useState('');
  const [statutVotantFilter, setStatutVotantFilter] = useState('');
  const [viewMode, setViewMode] = useState(() => {
    // Récupérer la préférence d'affichage du localStorage
    const savedView = localStorage.getItem('cardinalsViewMode');
    return savedView || 'grid';
  });
  const [showFiltersMobile, setShowFiltersMobile] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCardinaux, setTotalCardinaux] = useState(0);
  const [imageErrors, setImageErrors] = useState({});
  
  // Image de fallback pour les cardinaux sans photo
  const defaultImage = '/images/cardinal-default.png';
  
  // Charger tous les cardinaux une seule fois pour les listes de filtres
  useEffect(() => {
    const fetchAllCardinaux = async () => {
      try {
        const response = await axios.get('/api/cardinaux?limit=200');
        setAllCardinaux(response.data.cardinaux);
      } catch (err) {
        console.error('Erreur lors du chargement de tous les cardinaux:', err);
      }
    };
    
    fetchAllCardinaux();
  }, []);
  
  // Chargement des cardinaux filtrés
  useEffect(() => {
    const fetchCardinaux = async () => {
      try {
        setLoading(true);
        
        // Construire les paramètres de requête
        const params = new URLSearchParams();
        params.append('page', currentPage);
        params.append('limit', 20);
        
        if (searchTerm) {
          params.append('nom', searchTerm);
        }
        
        if (paysFilter) {
          params.append('paysService', paysFilter);
        }
        
        if (continentFilter) {
          params.append('continent', continentFilter);
        }
        
        if (ordreFilter) {
          params.append('ordre', ordreFilter);
        }
        
        if (papabileFilter !== '') {
          params.append('papabile', papabileFilter);
        }
        
        if (statutVotantFilter !== '') {
          params.append('statutVotant', statutVotantFilter);
        }
        
        const response = await axios.get(`/api/cardinaux/search?${params.toString()}`);
        
        setCardinaux(response.data.cardinaux);
        setFilteredCardinaux(response.data.cardinaux);
        setTotalPages(response.data.totalPages);
        setTotalCardinaux(response.data.total);
        setLoading(false);
      } catch (err) {
        console.error('Erreur lors du chargement des cardinaux:', err);
        setError('Erreur lors du chargement des données. Veuillez réessayer plus tard.');
        setLoading(false);
      }
    };
    
    fetchCardinaux();
  }, [currentPage, searchTerm, paysFilter, continentFilter, ordreFilter, papabileFilter, statutVotantFilter]);
  
  // Extraire tous les pays de service uniques à partir de allCardinaux
  const getUniquePaysService = () => {
    const paysSet = new Set();
    
    // Collecter uniquement les pays de service
    allCardinaux.forEach(cardinal => {
      if (cardinal.paysService) paysSet.add(cardinal.paysService);
    });
    
    return Array.from(paysSet).sort();
  };
  
  // Extraire tous les ordres uniques à partir de allCardinaux
  const getUniqueOrdres = () => {
    const ordresSet = new Set();
    
    allCardinaux.forEach(cardinal => {
      if (cardinal.ordre) ordresSet.add(cardinal.ordre);
    });
    
    return Array.from(ordresSet).sort();
  };
  
  // Fonction pour changer le mode d'affichage
  const handleViewModeChange = (mode) => {
    setViewMode(mode);
    localStorage.setItem('cardinalsViewMode', mode);
  };
  
  // Gestionnaire de recherche
  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1); // Revenir à la première page lors d'une recherche
  };
  
  // Réinitialiser les filtres
  const resetFilters = () => {
    setSearchTerm('');
    setPaysFilter('');
    setContinentFilter('');
    setOrdreFilter('');
    setPapabileFilter('');
    setStatutVotantFilter('');
    setCurrentPage(1);
  };
  
  // Pagination - Page précédente
  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
      window.scrollTo(0, 0);
    }
  };
  
  // Pagination - Page suivante
  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
      window.scrollTo(0, 0);
    }
  };
  
  // Utiliser la route API pour les images avec le bon content-type
  const getApiImageUrl = (originalUrl, cardinalId) => {
    if (!originalUrl || imageErrors[cardinalId]) return defaultImage;
    
    // Extraire le nom du fichier de l'URL
    const filename = originalUrl.split('/').pop().split('?')[0];
    
    // Ajouter un timestamp pour éviter le cache du navigateur
    const timestamp = new Date().getTime();
    return `/api/images/${filename}?t=${timestamp}`;
  };
  
  // Gestionnaire d'erreur pour les images
  const handleImageError = (cardinalId) => {
    console.log(`Erreur de chargement d'image pour cardinal ${cardinalId}, utilisation de l'image par défaut`);
    setImageErrors(prev => ({
      ...prev,
      [cardinalId]: true
    }));
  };
  
  // Obtenir la liste unique des pays de service
  const paysServiceList = getUniquePaysService();
  
  // Obtenir la liste unique des ordres
  const ordresList = getUniqueOrdres();
  
  // Liste des continents
  const continents = ['Afrique', 'Amérique', 'Asie', 'Europe', 'Océanie'];
  
  return (
    <div className="cardinaux-container">
      {/* Filtres et sélecteur de vue */}
      <div className="filters-and-view-mode">
        <div className="filter-toggle-mobile" onClick={() => setShowFiltersMobile(!showFiltersMobile)}>
          <IconFilter /> Filtres et recherche
        </div>
        
        <div className={`filters-container ${showFiltersMobile ? 'show' : ''}`}>
          <form onSubmit={handleSearch} className="search-bar">
            <IconSearch className="search-icon" />
            <input
              type="text"
              placeholder="Rechercher un cardinal..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button 
                type="button" 
                className="clear-search" 
                onClick={() => {
                  setSearchTerm('');
                  setCurrentPage(1);
                }}
                aria-label="Effacer la recherche"
              >
                ×
              </button>
            )}
            <button type="submit" className="search-button" style={{ display: 'none' }}>
              Rechercher
            </button>
          </form>
          
          <div className="filter-options">
            <select
              value={continentFilter}
              onChange={(e) => {
                setContinentFilter(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option value="">Tous les continents</option>
              {continents.map(continent => (
                <option key={continent} value={continent}>{continent}</option>
              ))}
            </select>
            
            <select
              value={paysFilter}
              onChange={(e) => {
                setPaysFilter(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option value="">Tous les pays</option>
              {paysServiceList.map(pays => (
                <option key={pays} value={pays}>{pays}</option>
              ))}
            </select>
            
            <select
              value={ordreFilter}
              onChange={(e) => {
                setOrdreFilter(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option value="">Tous les ordres</option>
              {ordresList.map(ordre => (
                <option key={ordre} value={ordre}>{ordre}</option>
              ))}
            </select>
            
            <select
              value={papabileFilter}
              onChange={(e) => {
                setPapabileFilter(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option value="">Tous les cardinaux</option>
              <option value="true">Papabile uniquement</option>
              <option value="false">Non papabile uniquement</option>
            </select>
            
            <select
              value={statutVotantFilter}
              onChange={(e) => {
                setStatutVotantFilter(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option value="">Tous les statuts</option>
              <option value="OUI">Électeurs uniquement</option>
              <option value="NON">Non-électeurs uniquement</option>
            </select>
            
            <button
              className="reset-filters-button"
              onClick={resetFilters}
              title="Réinitialiser les filtres"
            >
              Réinitialiser
            </button>
          </div>
        </div>
        
        <div className="view-mode-toggle">
          <button
            className={`view-mode-button ${viewMode === 'grid' ? 'active' : ''}`}
            onClick={() => handleViewModeChange('grid')}
            title="Vue en grille"
          >
            <IconThGrid />
          </button>
          <button
            className={`view-mode-button ${viewMode === 'list' ? 'active' : ''}`}
            onClick={() => handleViewModeChange('list')}
            title="Vue en liste"
          >
            <IconThList />
          </button>
        </div>
      </div>
      
      {/* Affichage des cardinaux */}
      {loading ? (
        <div className="loading">Chargement des cardinaux...</div>
      ) : error ? (
        <div className="error-message">{error}</div>
      ) : filteredCardinaux.length === 0 ? (
        <div className="no-results">
          <p>Aucun cardinal ne correspond à vos critères de recherche.</p>
          <button onClick={resetFilters} className="reset-search-button">
            Réinitialiser les filtres
          </button>
        </div>
      ) : viewMode === 'grid' ? (
        <div className="cardinaux-grid">
          {filteredCardinaux.map(cardinal => (
            <div key={cardinal._id} className="cardinal-card">
              <Link to={`/cardinaux/${cardinal.slug}`}>
                <img 
                  src={getApiImageUrl(cardinal.photo, cardinal._id)} 
                  alt={cardinal.nomComplet} 
                  onError={() => handleImageError(cardinal._id)}
                />
                <h3>{cardinal.nomComplet}</h3>
                <p>{cardinal.titre || cardinal.ordre}</p>
                <p>{cardinal.age} ans • {cardinal.paysService}</p>
                <div className="cardinal-badges">
                  {cardinal.papabile && (
                    <span className="papabile-badge">Papabile</span>
                  )}
                  {cardinal.statutVotant === 'OUI' && (
                    <span className="votant-badge">Électeur</span>
                  )}
                </div>
              </Link>
            </div>
          ))}
        </div>
      ) : (
        <div className="cardinaux-list">
          {filteredCardinaux.map(cardinal => (
            <div key={cardinal._id} className="cardinal-list-item">
              <Link to={`/cardinaux/${cardinal.slug}`} className="cardinal-list-link">
                <div className="cardinal-list-photo">
                  <img 
                    src={getApiImageUrl(cardinal.photo, cardinal._id)} 
                    alt={cardinal.nomComplet} 
                    onError={() => handleImageError(cardinal._id)}
                  />
                </div>
                <div className="cardinal-list-info">
                  <h3>{cardinal.nomComplet}</h3>
                  <div className="cardinal-list-details">
                    <p><strong>Titre:</strong> {cardinal.titre || cardinal.ordre}</p>
                    <p><strong>Âge:</strong> {cardinal.age} ans</p>
                    <p><strong>Pays d'origine:</strong> {cardinal.paysNaissance}</p>
                    <p><strong>Pays de service:</strong> {cardinal.paysService}</p>
                    <p><strong>Continent:</strong> {cardinal.continent}</p>
                  </div>
                </div>
                <div className="cardinal-list-badges">
                  {cardinal.papabile && (
                    <span className="papabile-badge">Papabile</span>
                  )}
                  {cardinal.statutVotant === 'OUI' && (
                    <span className="votant-badge">Électeur</span>
                  )}
                </div>
              </Link>
            </div>
          ))}
        </div>
      )}
      
      {/* Pagination */}
      {!loading && filteredCardinaux.length > 0 && totalPages > 1 && (
        <div className="pagination">
          <button 
            onClick={goToPreviousPage} 
            disabled={currentPage === 1}
            className="pagination-button"
          >
            &laquo; Précédent
          </button>
          <span className="pagination-info">
            Page {currentPage} sur {totalPages}
          </span>
          <button 
            onClick={goToNextPage} 
            disabled={currentPage === totalPages}
            className="pagination-button"
          >
            Suivant &raquo;
          </button>
        </div>
      )}
      
      {/* Compteur de résultats */}
      {!loading && filteredCardinaux.length > 0 && (
        <div className="results-count">
          Affichage de {filteredCardinaux.length} cardinal(aux) sur {totalCardinaux} au total
        </div>
      )}
    </div>
  );
};

export default CardinalsList;